// ============================================
// Earth Rendering
// Three.js Earth with clouds and atmosphere for Low Orbit stage
// ============================================

import type { Group, Mesh, ShaderMaterial } from './types';
import { CONFIG } from '../../../config/game-config';
import { getP5 } from '../../../core/p5-instance';
import { addToScene, removeFromScene } from './scene';
import {
    earthVertexShader,
    earthFragmentShader,
    cloudVertexShader,
    cloudFragmentShader,
    atmosphereVertexShader,
    atmosphereFragmentShader
} from './shaders';

// Earth constants
const EARTH_RADIUS = 275;
const CLOUD_RADIUS = EARTH_RADIUS * 1.015;
const ATMO_RADIUS = EARTH_RADIUS * 1.12;

// Earth objects
let earthGroup: Group | null = null;
let earthMesh: Mesh | null = null;
let cloudMesh: Mesh | null = null;
let atmosphereMesh: Mesh | null = null;

// Shared sun direction
let sunDirection: ReturnType<typeof THREE.Vector3.prototype.normalize> | null = null;

/**
 * Get the Earth group
 */
export function getEarthGroup(): Group | null {
    return earthGroup;
}

/**
 * Create Earth with clouds and atmosphere (view from space)
 */
export function createThreeEarth(): void {
    earthGroup = new THREE.Group();

    // Shared sun direction for all shaders
    sunDirection = new THREE.Vector3(-1.0, 0.2, 0.5).normalize();

    // ===================================================
    // Earth surface (oceans and continents) - Layer 1
    // ===================================================
    const earthGeometry = new THREE.SphereGeometry(EARTH_RADIUS, 96, 64);
    const earthMaterial = new THREE.ShaderMaterial({
        uniforms: {
            time: { value: 0 },
            sunDirection: { value: sunDirection }
        },
        vertexShader: earthVertexShader,
        fragmentShader: earthFragmentShader
    });
    earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
    earthGroup.add(earthMesh);

    // ===================================================
    // Cloud layer (independent sphere) - Layer 2
    // ===================================================
    const cloudGeometry = new THREE.SphereGeometry(CLOUD_RADIUS, 96, 64);
    const cloudMaterial = new THREE.ShaderMaterial({
        uniforms: {
            time: { value: 0 },
            sunDirection: { value: sunDirection }
        },
        transparent: true,
        depthWrite: false,
        side: THREE.FrontSide,
        vertexShader: cloudVertexShader,
        fragmentShader: cloudFragmentShader
    });
    cloudMesh = new THREE.Mesh(cloudGeometry, cloudMaterial);
    earthGroup.add(cloudMesh);

    // ===================================================
    // Atmosphere layer (Fresnel gradient effect)
    // Smoothly blurs the boundary between space and Earth
    // ===================================================
    const atmosphereGeometry = new THREE.SphereGeometry(ATMO_RADIUS, 64, 32);
    const atmosphereMaterial = new THREE.ShaderMaterial({
        uniforms: {
            sunDirection: { value: sunDirection }
        },
        transparent: true,
        depthWrite: false,
        side: THREE.BackSide,
        blending: THREE.AdditiveBlending,
        vertexShader: atmosphereVertexShader,
        fragmentShader: atmosphereFragmentShader
    });
    atmosphereMesh = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    earthGroup.add(atmosphereMesh);

    earthGroup.visible = false;
    addToScene(earthGroup);
}

/**
 * Update Earth visibility and animation
 */
export function updateThreeEarth(
    showEarth: boolean,
    bgPlanetY: number,
    shakeX: number,
    shakeY: number
): void {
    if (!earthGroup) {
        return;
    }

    earthGroup.visible = showEarth;

    if (showEarth) {
        const p = getP5();

        // Position calculation
        const threeX = CONFIG.width - 20 - CONFIG.width / 2;
        const threeY = CONFIG.height / 2 - (bgPlanetY - 150);
        earthGroup.position.set(threeX + shakeX, threeY - shakeY, -100);

        // Time value for animation
        const timeValue = p.frameCount * 0.01;

        // Earth rotation and uniform update
        if (earthMesh) {
            earthMesh.rotation.y += 0.0005;
            const material = earthMesh.material as ShaderMaterial;
            if (material.uniforms?.time) {
                material.uniforms.time.value = timeValue;
            }
        }

        // Cloud layer rotation (slightly faster than Earth)
        if (cloudMesh) {
            cloudMesh.rotation.y += 0.00065;
            const material = cloudMesh.material as ShaderMaterial;
            if (material.uniforms?.time) {
                material.uniforms.time.value = timeValue;
            }
        }
    }
}

/**
 * Reset Earth rotation
 */
export function resetEarthRotation(): void {
    if (earthMesh) {
        earthMesh.rotation.y = 0;
    }
    if (cloudMesh) {
        cloudMesh.rotation.y = 0;
    }
}

/**
 * Dispose of Earth resources
 */
export function disposeThreeEarth(): void {
    if (earthGroup) {
        // Dispose Earth mesh
        if (earthMesh) {
            if (earthMesh.geometry) {
                earthMesh.geometry.dispose();
            }
            if (earthMesh.material) {
                (earthMesh.material as ShaderMaterial).dispose();
            }
            earthMesh = null;
        }

        // Dispose cloud mesh
        if (cloudMesh) {
            if (cloudMesh.geometry) {
                cloudMesh.geometry.dispose();
            }
            if (cloudMesh.material) {
                (cloudMesh.material as ShaderMaterial).dispose();
            }
            cloudMesh = null;
        }

        // Dispose atmosphere mesh
        if (atmosphereMesh) {
            if (atmosphereMesh.geometry) {
                atmosphereMesh.geometry.dispose();
            }
            if (atmosphereMesh.material) {
                (atmosphereMesh.material as ShaderMaterial).dispose();
            }
            atmosphereMesh = null;
        }

        removeFromScene(earthGroup);
        earthGroup = null;
    }

    sunDirection = null;
}
